import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useCreateSalesforceUserMappingMutation,
	useDeleteSalesforceUserMappingMutation,
	useGetSalesforceOwnersQuery,
	useGetSalesforceUserMappingsQuery,
} from "@/pages/Private/redux/salesforce/salesforce.api";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

interface LocalMapping {
	userId: number;
	salesforceOwnerId: string;
	salesforceOwnerName?: string;
}

/**
 * Signalist user ↔ Salesforce `User` mapping, which decides who owns the records the sync
 * writes.
 *
 * The consequence differs from the Dynamics tab's and the copy says so: a Dataverse annotation
 * with no owner is invisible, so that sync *skips* the batch. A Salesforce `Task` written
 * without `OwnerId` is simply owned by the connected user and still appears on the Activity
 * timeline — so nothing is skipped, but an unmapped sender's conversation lands under the wrong
 * person, which is quieter and therefore easier to miss.
 */
export const SalesforceUserMappingTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.salesforce.userMapping.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: sendersData } = useGetSendersUsersQuery();
	const { data: ownersData } = useGetSalesforceOwnersQuery();
	const { data: userMappingsData, isLoading: isLoadingMappings } =
		useGetSalesforceUserMappingsQuery();
	const [createUserMapping] = useCreateSalesforceUserMappingMutation();
	const [deleteUserMapping] = useDeleteSalesforceUserMappingMutation();

	const [mappings, setMappings] = useState<LocalMapping[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (userMappingsData) {
			setMappings(
				userMappingsData.map((mapping) => ({
					userId: mapping.userId,
					salesforceOwnerId: mapping.salesforceOwnerId,
					salesforceOwnerName: mapping.salesforceOwnerName ?? undefined,
				}))
			);
		}
	}, [userMappingsData]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	const handleOwnerChange = (userId: number, ownerId: string) => {
		const owner = ownersData?.owners?.find((candidate) => candidate.id === ownerId);

		setMappings((prev) => {
			const existing = prev.find((mapping) => mapping.userId === userId);

			if (existing) {
				return prev.map((mapping) =>
					mapping.userId === userId
						? { ...mapping, salesforceOwnerId: ownerId, salesforceOwnerName: owner?.name }
						: mapping
				);
			}

			return [...prev, { userId, salesforceOwnerId: ownerId, salesforceOwnerName: owner?.name }];
		});
	};

	const handleSave = async () => {
		try {
			setIsSaving(true);

			const serverMappings = userMappingsData ?? [];

			// There is no update endpoint — a changed owner is a delete plus a create.
			const toDelete = serverMappings.filter((server) => {
				const local = mappings.find((mapping) => mapping.userId === server.userId);

				return !local || local.salesforceOwnerId !== server.salesforceOwnerId;
			});

			const toCreate = mappings.filter((mapping) => {
				const server = serverMappings.find((candidate) => candidate.userId === mapping.userId);

				return !server || server.salesforceOwnerId !== mapping.salesforceOwnerId;
			});

			await Promise.all(toDelete.map((mapping) => deleteUserMapping(mapping.id).unwrap()));

			await Promise.all(
				toCreate.map((mapping) =>
					createUserMapping({
						userId: mapping.userId,
						salesforceOwnerId: mapping.salesforceOwnerId,
						salesforceOwnerName: mapping.salesforceOwnerName,
					}).unwrap()
				)
			);

			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		} finally {
			setIsSaving(false);
		}
	};

	const senders = sendersData?.data ?? [];

	if (isLoadingMappings) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex w-full flex-col gap-4">
			{renderMessageSnackbar()}
			<div className="mb-1 text-sm text-text-meta">{ts("description")}</div>

			{senders.length === 0 ? (
				<div className="py-6 text-center text-text-meta">{ts("noUsers")}</div>
			) : (
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-4 border-b border-border-light py-2 text-sm font-medium text-text-meta">
						<div className="flex-1">{ts("signalistUser")}</div>
						<div className="flex-1">{ts("salesforceOwner")}</div>
					</div>

					{senders.map((user) => {
						const currentMapping = mappings.find((mapping) => mapping.userId === user.id);

						return (
							<div
								key={user.id}
								className="flex items-center gap-4 border-b border-border-light py-2"
							>
								<div className="flex-1 text-sm text-text">
									{user.firstName} {user.lastName} ({user.email})
								</div>
								<div className="flex-1">
									<Dropdown
										data={ownerOptions}
										defaultValue={ownerOptions.find(
											(option) => option.id === currentMapping?.salesforceOwnerId
										)}
										floating={true}
										handleSelect={(value) => handleOwnerChange(user.id, value.id as string)}
										label=""
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<div className="mt-4 flex justify-end">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					testId="salesforce-user-mapping-save"
					title={t("basics.save")}
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
